// Team colour helpers shared across the Org Graph (force-directed) and the
// Org Tree (top-down hierarchy) views.
//
// Both views colour each node card by the team the person belongs to:
//   - members of a team       → their manager's id
//   - team leaders (roots with reports) → their own id
//   - orphans (no manager, no reports)  → the shared ORPHAN_TEAM_ID bucket
//
// Pure helpers — no React, no state. Lifted out of `OrgGraph.tsx` (where they
// were originally defined inline) so the Org Tree view can reuse them without
// importing from a sibling component module.

import type { IOrgGraphNode } from '@skystone/shared';

/**
 * Saturated palette assigned to teams (manager + their direct reports) by
 * alphabetical index of the team's leader. Saturated hues so neighbouring
 * pods are visually distinct on the org chart background.
 *
 * Team membership = your manager's team; a person who is also a manager
 * leads their own team and is coloured by THEIR manager's team (so they
 * stand out as a different-coloured node inside their own team's hull).
 */
export const TEAM_PALETTE: readonly string[] = [
  '#2563eb', // blue-600
  '#16a34a', // green-600
  '#ea580c', // orange-600
  '#dc2626', // red-600
  '#9333ea', // purple-600
  '#0891b2', // cyan-600
  '#ca8a04', // yellow-600
  '#db2777', // pink-600
];

/**
 * Special cluster id for orphans — people with no manager AND no direct
 * reports. They get bucketed together so the force layout doesn't scatter
 * them across the canvas, but they are NOT a "team" and don't take a slot
 * in the saturated team palette. See PRODOPSAI-465 follow-up.
 */
export const ORPHAN_TEAM_ID = '__orphans__';

/**
 * Muted colour for orphan nodes (no manager, no reports). Visually de-
 * emphasised so they don't compete with real teams. CSS variable resolves
 * to the design system's muted-foreground.
 */
export const ORPHAN_COLOR = 'var(--color-muted-foreground)';

/**
 * Team id for the purposes of clustering and colouring a node.
 *   - Members of a team       → their manager's id
 *   - Team leaders (roots with reports) → their own id
 *   - Orphans (no manager, no reports) → the shared ORPHAN_TEAM_ID so the
 *     force layout pulls them together
 *
 * Pure helper — no React. `hasReports` is a precomputed set so the caller
 * controls iteration cost (we only build it once per render).
 */
export function teamIdForNode(
  node: { id: string; managerUpn: string | null },
  hasReports: Set<string>,
): string {
  if (node.managerUpn !== null) return node.managerUpn;
  if (hasReports.has(node.id)) return node.id;
  return ORPHAN_TEAM_ID;
}

/**
 * Build a stable team-id → palette-colour map for the supplied node set.
 *
 * Teams are sorted alphabetically by the team-leader's displayName so palette
 * assignment is deterministic and predictable (the same leader always gets
 * the same colour for a given roster). Hash-modulo would let multiple teams
 * collide on the same palette index, which made all clusters look identical
 * on the previous department-based design.
 *
 * The orphan bucket (ORPHAN_TEAM_ID) is intentionally excluded — orphans
 * get the fixed ORPHAN_COLOR instead of competing for a palette slot.
 */
export function buildTeamColorMap(
  nodes: IOrgGraphNode[],
  hasReports: Set<string>,
): Map<string, string> {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const teamIds = new Set<string>();
  for (const node of nodes) {
    const teamId = teamIdForNode(node, hasReports);
    if (teamId === ORPHAN_TEAM_ID) continue;
    teamIds.add(teamId);
  }
  const sorted = [...teamIds].sort((a, b) => {
    const aName = nodeById.get(a)?.displayName ?? a;
    const bName = nodeById.get(b)?.displayName ?? b;
    return aName.localeCompare(bName);
  });
  const map = new Map<string, string>();
  sorted.forEach((teamId, i) => {
    map.set(teamId, TEAM_PALETTE[i % TEAM_PALETTE.length]);
  });
  return map;
}
